import { ApiError } from '@/lib/api/errors.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useState } from 'react';
import { useCreateSavedView } from '../hooks/use-create-saved-view.js';
import type { SavedViewState } from '../lib/saved-views-client.js';

interface SaveViewDialogProps {
  open: boolean;
  onClose: () => void;
  tenantReportId: string;
  currentState: SavedViewState;
  canSaveSharedView: boolean;
}

export function SaveViewDialog({
  open,
  onClose,
  tenantReportId,
  currentState,
  canSaveSharedView,
}: SaveViewDialogProps) {
  const [name, setName] = useState(() => `Vista del ${new Date().toLocaleDateString('es-ES')}`);
  const [scope, setScope] = useState<'personal' | 'shared'>('personal');
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useCreateSavedView({ tenantReportId });

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);

    try {
      await mutation.mutateAsync({
        name: name.trim(),
        scope,
        state: currentState,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setServerError('Ya existe una vista con ese nombre. Elige otro nombre.');
      } else if (err instanceof Error) {
        setServerError(err.message);
      } else {
        setServerError('Error al guardar la vista.');
      }
    }
  }

  function handleClose() {
    setServerError(null);
    mutation.reset();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — button for keyboard-accessible dismiss */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        aria-label="Dismiss dialog"
        onClick={handleClose}
      />

      {/* Panel */}
      {/* biome-ignore lint/a11y/useSemanticElements: stacked within a fixed backdrop; native <dialog> would require showModal() imperative API which conflicts with React state */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-view-dialog-title"
        className={cn(
          'relative w-full max-w-md rounded-[var(--radius-card)]',
          'border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)]',
          'shadow-[var(--shadow-card)] p-6',
        )}
      >
        <h2
          id="save-view-dialog-title"
          className="mb-4 text-base font-semibold text-[var(--color-qup-text-dark)]"
        >
          {t('saved_views.dialog_save_title')}
        </h2>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          {/* Name field */}
          <div className="mb-4">
            <label
              htmlFor="save-view-name"
              className="mb-1 block text-sm font-medium text-[var(--color-qup-text-dark)]"
            >
              {t('saved_views.field_name')}
            </label>
            <input
              id="save-view-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={255}
              className={cn(
                'w-full rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)]',
                'bg-[var(--color-qup-bg-dark)] px-3 py-2 text-sm text-[var(--color-qup-text-dark)]',
                'placeholder:text-[var(--color-qup-text-muted-dark)]',
                'focus:outline-none focus:ring-2 focus:ring-[var(--color-qup-primary)]',
              )}
            />
          </div>

          {/* Scope field */}
          <fieldset className="mb-5">
            <legend className="mb-2 text-sm font-medium text-[var(--color-qup-text-dark)]">
              {t('saved_views.field_scope')}
            </legend>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value="personal"
                  checked={scope === 'personal'}
                  onChange={() => setScope('personal')}
                  className="accent-[var(--color-qup-primary)]"
                />
                <span className="text-sm text-[var(--color-qup-text-dark)]">
                  {t('saved_views.scope_personal')}
                </span>
              </label>

              <div className="relative">
                <label
                  className={cn(
                    'flex items-center gap-2',
                    canSaveSharedView ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                  )}
                >
                  <input
                    type="radio"
                    name="scope"
                    value="shared"
                    checked={scope === 'shared'}
                    onChange={() => setScope('shared')}
                    disabled={!canSaveSharedView}
                    className="accent-[var(--color-qup-primary)]"
                  />
                  <span className="text-sm text-[var(--color-qup-text-dark)]">
                    {t('saved_views.scope_shared')}
                  </span>
                </label>
                {!canSaveSharedView && (
                  <p className="mt-0.5 ml-6 text-xs text-[var(--color-qup-text-muted-dark)]">
                    {t('saved_views.scope_shared_disabled_hint')}
                  </p>
                )}
              </div>
            </div>
          </fieldset>

          {/* Server error */}
          {serverError !== null && (
            <div
              role="alert"
              className="mb-4 rounded-[var(--radius-button)] border border-[var(--color-qup-primary)]/30 bg-[var(--color-qup-primary)]/10 px-3 py-2 text-sm text-[var(--color-qup-primary)]"
            >
              {serverError}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className={cn(
                'rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)]',
                'px-4 py-2 text-sm font-medium text-[var(--color-qup-text-dark)]',
                'hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
              )}
            >
              {t('saved_views.dialog_close')}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || name.trim().length === 0}
              className={cn(
                'rounded-[var(--radius-button)] bg-[var(--color-qup-primary)] px-4 py-2 text-sm font-medium text-white',
                'hover:bg-[var(--color-qup-primary-hover)] transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {mutation.isPending ? 'Guardando…' : t('saved_views.dialog_save_button')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
